"use client";
import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import {
  getEventsHref,
  getMerchantHref,
  getPromotionHref,
} from "@/constants/hooks";
import { FontAwesomeIcon } from "@/constants/icons";
import { faChevronDown, faTimes } from "@fortawesome/free-solid-svg-icons";

const LazySearchBar = React.lazy(() => import('./SearchBar'));

interface Props {
  companyId: string;
  companyLogo: string;
  companyDomain: string;
  categories?: any[];
  merchantData?: any[];
  events?: any[];
  promotions?: any[];
  blog_title?: string | null;
  blog_url?: string | null;
  mer_slug: string;
  cat_slug: string;
  mer_slug_type: string;
  promo_slug: string;
}

export default function MobileNavWrapper({
  companyId,
  companyLogo,
  categories = [],
  merchantData = [],
  events = [],
  promotions = [],
  blog_title,
  blog_url,
  mer_slug,
  cat_slug,
  mer_slug_type,
  promo_slug,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const toggle = useCallback(() => {
    const next = !isOpen;
    setIsOpen(next);
    document.body.style.overflow = next ? "hidden" : "";
  }, [isOpen]);

  const handleToggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setOpenSection(null);
    document.body.style.overflow = "";
  }, []);

  const renderSection = (
    title: string,
    sectionName: string,
    items: any[],
    renderLink: (item: any) => { href: string; label: string },
    viewAllHref?: string
  ) => {
    if (!items || !Array.isArray(items) || items.length === 0) return null;
    const expanded = openSection === sectionName;
    return (
      <li style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            cursor: 'pointer',
            fontWeight: 700,
            color: expanded ? '#39ff14' : 'rgba(255,255,255,0.8)',
            background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
            transition: 'color 0.2s, background 0.2s',
          }}
          onClick={() => handleToggleSection(sectionName)}
        >
          <span style={{ fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{title}</span>
          <span style={{
            width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${expanded ? 'rgba(57,255,20,0.3)' : 'rgba(255,255,255,0.1)'}`,
            background: expanded ? 'rgba(57,255,20,0.15)' : 'rgba(255,255,255,0.05)',
            color: expanded ? '#39ff14' : 'rgba(255,255,255,0.4)',
            transition: 'all 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faChevronDown} style={{ width: 10, height: 10 }} />
          </span>
        </div>

        {/* Expanded items — no max-height cap, just show/hide */}
        {expanded && (
          <div style={{ background: 'rgba(0,0,0,0.2)', paddingBottom: 8 }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: '8px 24px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.slice(0, 10).map((item, i) => {
                const { href, label } = renderLink(item);
                return (
                  <li key={i}>
                    <Link
                      href={href}
                      onClick={closeMenu}
                      style={{
                        display: 'block',
                        padding: '10px 0',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.6)',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        transition: 'color 0.15s',
                      }}
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
              {viewAllHref && (
                <li>
                  <Link
                    href={viewAllHref}
                    onClick={closeMenu}
                    style={{
                      display: 'inline-block',
                      padding: '8px 0',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#39ff14',
                      textDecoration: 'none',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      marginTop: 8,
                    }}
                  >
                    View All {title} →
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}
      </li>
    );
  };

  const drawerContent = (
    <>
      {/* Invisible scrollbar style */}
      <style>{`
        .mnav-body::-webkit-scrollbar { display: none; }
        .mnav-body { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      {/* Overlay */}
      <div
        onClick={closeMenu}
        style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(4px)',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s, visibility 0.3s',
        }}
      />

      {/* Drawer — explicit pixel height via dvh with fallback */}
      <aside
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '82vw',
          maxWidth: 340,
          /* Use dvh (dynamic viewport height) so mobile browser chrome doesn't clip */
          height: '100dvh',
          /* Fallback for browsers without dvh support */
          minHeight: '-webkit-fill-available',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #0b110a 0%, #0a0f0a 100%)',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '8px 0 40px rgba(0,0,0,0.6)',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out',
          /* Critical: overflow must be hidden on the aside itself */
          overflow: 'hidden',
        }}
      >
        {/* ── Header (fixed height, never shrinks) ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px', flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(11,17,10,0.5)',
        }}>
          <Link href="/" onClick={closeMenu} style={{ position: 'relative', display: 'block', height: 32, width: 112 }}>
            {companyLogo ? (
              <Image
                src={companyLogo}
                alt="Logo"
                fill
                className="object-contain object-left"
                sizes="112px"
              />
            ) : (
              <span style={{ color: '#39ff14', fontSize: 18, fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                LOGO
              </span>
            )}
          </Link>
          <button
            onClick={closeMenu}
            aria-label="Close menu"
            style={{
              width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* ── Search (fixed height, never shrinks) ── */}
        <div style={{
          padding: 16, flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(11,17,10,0.3)',
        }}>
          <React.Suspense fallback={
            <div style={{ width: '100%', height: 40, background: 'rgba(255,255,255,0.05)', borderRadius: 999, border: '1px solid rgba(255,255,255,0.1)' }} />
          }>
            <LazySearchBar
              companyId={companyId}
              mer_slug={mer_slug}
              slug_type={mer_slug_type}
              cat_slug={cat_slug}
            />
          </React.Suspense>
        </div>

        {/* ── Scrollable nav body ── 
            flex: 1 1 0 means it takes ALL remaining height.
            overflow-y: auto + the CSS above hides the scrollbar. ── */}
        <div
          className="mnav-body"
          style={{
            flex: '1 1 0',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <nav style={{ padding: '16px 16px 80px 16px' }}>
            <ul style={{
              listStyle: 'none', margin: 0, padding: 0,
              display: 'flex', flexDirection: 'column',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16,
              overflow: 'hidden',
            }}>
              {/* Home */}
              <li style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Link
                  href="/"
                  onClick={closeMenu}
                  style={{
                    display: 'block', padding: '16px 20px',
                    fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.8)', textDecoration: 'none',
                    transition: 'color 0.15s',
                  }}
                >
                  Home
                </Link>
              </li>

              {renderSection("Stores", "stores", merchantData, (m) => ({
                href: getMerchantHref(m, mer_slug, mer_slug_type),
                label: m.merchant_name,
              }), "/all-stores/A")}

              {renderSection("Categories", "categories", categories, (c) => ({
                href: `/${c?.url || c?.category_slug}`,
                label: c.name || c.category_name,
              }), `/${cat_slug}`)}

              {renderSection("Events", "events", events, (e) => ({
                href: getEventsHref(e, "slug"),
                label: e.name,
              }), "/events")}

              {renderSection("Promotions", "promotions", promotions, (p) => ({
                href: getPromotionHref(p, promo_slug),
                label: p.name,
              }), `/${promo_slug}`)}

              {/* Products */}
              <li style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', cursor: 'pointer', fontWeight: 700,
                    color: openSection === 'products' ? '#39ff14' : 'rgba(255,255,255,0.8)',
                    background: openSection === 'products' ? 'rgba(255,255,255,0.03)' : 'transparent',
                    transition: 'color 0.2s',
                  }}
                  onClick={() => handleToggleSection("products")}
                >
                  <span style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Products</span>
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${openSection === 'products' ? 'rgba(57,255,20,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    background: openSection === 'products' ? 'rgba(57,255,20,0.15)' : 'rgba(255,255,255,0.05)',
                    color: openSection === 'products' ? '#39ff14' : 'rgba(255,255,255,0.4)',
                    transform: openSection === 'products' ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'all 0.2s', flexShrink: 0,
                  }}>
                    <FontAwesomeIcon icon={faChevronDown} style={{ width: 10, height: 10 }} />
                  </span>
                </div>
                {openSection === 'products' && (
                  <div style={{ background: 'rgba(0,0,0,0.2)', paddingBottom: 8 }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: '8px 24px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <li>
                        <Link href="/products" onClick={closeMenu} style={{ display: 'block', padding: '10px 0', fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>
                          Brands Products
                        </Link>
                      </li>
                      <li>
                        <Link href="/all-products" onClick={closeMenu} style={{ display: 'inline-block', padding: '8px 0', fontSize: 11, fontWeight: 700, color: '#39ff14', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 8 }}>
                          View All Products →
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
            </ul>

            {blog_title && blog_url && (
              <div style={{ marginTop: 32 }}>
                <Link
                  href={blog_url}
                  target="_blank"
                  onClick={closeMenu}
                  style={{
                    display: 'block', width: '100%', padding: '14px 20px',
                    fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: '#081C11', background: '#39ff14',
                    borderRadius: 12, textAlign: 'center', textDecoration: 'none',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                  }}
                >
                  {blog_title}
                </Link>
              </div>
            )}
          </nav>
        </div>
      </aside>
    </>
  );

  return (
    <>
      {/* Hamburger Button */}
      <button
        type="button"
        aria-label="Toggle mobile menu"
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)',
          color: '#39ff14', cursor: 'pointer', transition: 'background 0.2s',
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: 20, height: 20 }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
      </button>

      {mounted && createPortal(drawerContent, document.body)}
    </>
  );
}
